import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import { getDateTime } from 'lib/utils';
import { useGetVoiceReportDetail } from 'services/voice-scan/hooks';
import Loader from 'components/Loader';
import { Button } from 'components/ui/button';
import { PATH_ROUTES } from 'routes/path';
import CircularProgress from 'components/CircularProgress';
import ScanDetail from '../report/ScanDetail';

const getScoreKey = (score: number) => {
  if (score >= 0 && score <= 20) return '1_2';
  if (score >= 21 && score <= 40) return '3_4';
  if (score >= 41 && score <= 60) return '5_6';
  if (score >= 61 && score <= 80) return '7_8';
  if (score >= 81 && score <= 100) return '9_10';
  return null;
};

export default function VoiceScanDetail() {
  const { id: reportId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: voiceReportDetail, isFetching } = useGetVoiceReportDetail({
    sessionId: reportId!,
  });

  const reportTime = useMemo(
    () =>
      voiceReportDetail?.report_generation_time
        ? getDateTime(voiceReportDetail?.report_generation_time)
        : null,
    [voiceReportDetail?.report_generation_time]
  );

  if (isFetching) {
    return <Loader />;
  }

  const onBack = () => {
    navigate(PATH_ROUTES.VOICE_SCAN);
  };

  const scoreKey = getScoreKey(voiceReportDetail?.wellness_score ?? 0);

  return (
    <div className='relative w-full h-full px-6 pt-2 pb-4'>
      <Button
        variant='outline'
        className='h-8 px-6 text-sm rounded-full text-primary hover:text-primary border-primary'
        onClick={onBack}
      >
        <ArrowLeft className='w-4 h-4 mb-px mr-1' /> {t('go_back')}
      </Button>

      <div className='flex items-center justify-between px-4 pt-2 pb-8 mt-2 lg:pt-0 sm:pt-4 lg:pb-4 h-fit lg:h-32 sm:px-10 gap-x-4 bg-primary-dark'>
        <div className='text-white'>
          <h1 className='mt-2 text-lg font-semibold sm:text-2xl'>
            {t('wellness_score_heading')}: {voiceReportDetail?.wellness_score}
          </h1>
          <p className='text-sm font-semibold sm:text-base'>
            {t(`wellness_score_heading_for_${scoreKey}`)}
          </p>
          <p className='mt-1 text-sm leading-none'>
            {t(`wellness_score_subheading_for_${scoreKey}`)}
          </p>
        </div>
        <div className='size-20 shrink-0'>
          <CircularProgress score={voiceReportDetail?.wellness_score || 0} />
        </div>
      </div>

      <div className='-mt-4 bg-white border rounded-2xl border-primary'>
        {reportTime ? (
          <div className='flex justify-center p-2 pr-6 sm:justify-end'>
            <p className='text-sm font-medium'>
              {t('report_generated_on')}: {reportTime.date} {reportTime.time}
            </p>
          </div>
        ) : null}

        <ScanDetail
          readingId={reportId!}
          className='h-screen lg:h-[calc(100vh-21.5rem)] -mt-6'
          containerClassName='border-none pl-4 sm:pl-6'
          isDetail
        />
      </div>
    </div>
  );
}
